import { Fragment, HTMLProps, useEffect, useRef, useState } from "react";

import { createColumnHelper, flexRender, getCoreRowModel, getExpandedRowModel, getSortedRowModel, SortingState, useReactTable } from "@tanstack/react-table";

import Modal from "../../layouts/Modal";
import { IPartCategory } from "../../models/Part";
import openInNewTab from "../../utils/openInNewTab";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import PriceFormat from './../../utils/priceFormat';
import BulkUpdateCategoryDefaults from "./BulkUpdate/BulkUpdateCategoryDefaults";
import BulkUpdateSingleCategoryDefault from "./BulkUpdate/BulkUpdateSingleCategoryDefault";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";

const colHelper = createColumnHelper<IPartCategory>();

const cols = [
  TableCheckboxCol(false),
  colHelper.accessor("name", {
    header: "Name",
    sortingFn: "auto",
  }),
  colHelper.accessor("partOnly", {
    header: "Body Part",
    cell: (info) => <span>{info.getValue() ? "✔" : ""}</span>,
  }),
  colHelper.accessor("mechOnly", {
    header: "Mech Part",
    cell: (info) => <span>{info.getValue() ? "✔" : ""}</span>,
  }),
  colHelper.accessor("frontierName", {
    header: "Frontier Name",
    cell: (info) => <span>{info.getValue() ? "✔" : ""}</span>,
  }),
  colHelper.accessor("pinnacleName", {
    header: "Pinnacle Name",
    cell: (info) => <span>{info.getValue() ? "✔" : ""}</span>,
  }),
  colHelper.accessor("whTime", {
    header: "Warehouse Time",
  }),
  colHelper.accessor("whCost", {
    header: "Warehouse Cost",
    cell: (info) => <span>£{info.getValue()}</span>,
  }),
  colHelper.accessor("disTime", {
    header: "Disassembly Time",
  }),
  colHelper.accessor("disCost", {
    header: "Disassembly Cost",
    cell: (info) => <span>£{info.getValue()}</span>,
  }),
  colHelper.accessor("postage", {
    header: "Postage Cost",
    cell: (info) => <span>{PriceFormat(info.getValue())}</span>,
  }),
  colHelper.accessor((row: any) => row.mappingsCount ?? row.partMappings?.length ?? 0, {
    id: "mappingsCount",
    header: "# of Mappings",
    sortingFn: "auto",
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  colHelper.accessor((row: any) => row.hasCategoryDefaults ?? ((row.partCategoryDefaults?.length ?? 0) > 0), {
    id: "hasCategoryDefaults",
    header: "Category Defaults",
    cell: (info) => <span>{info.getValue() ? "✔" : ""}</span>,
  }),
  colHelper.accessor((row: any) => row.hasPartAssembly ?? ((row.partAssemblyPartCategories?.length ?? 0) > 0), {
    id: "partAssemblyPartCategories",
    header: "Part Assembly",
    cell: (info) => <span>{info.getValue() ? "✔" : ""}</span>,
  }),
];


export default function CategoryTable({ categories, setCategory }: { categories: IPartCategory[], setCategory: any }) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedCategories, setSelectedCategories] = useState<IPartCategory[]>([]);
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkSingleUpdateOpen, setBulkSingleUpdateOpen] = useState(false);

  const handleBulkUpdate = () => {
    const selectCategories = table.getSelectedRowModel().flatRows.map((row) => row.original);

    setSelectedCategories(selectCategories);
    setBulkUpdateOpen(true);
  };

  const handleBulkSingleUpdate = () => {
    const selectCategories = table.getSelectedRowModel().flatRows.map((row) => row.original);

    setSelectedCategories(selectCategories);
    setBulkSingleUpdateOpen(true);
  };

  const table = useReactTable({
    data: categories,
    columns: cols,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <BulkUpdateCategoryDefaults open={bulkUpdateOpen} setOpen={setBulkUpdateOpen} selectedCategories={selectedCategories} />
      <BulkUpdateSingleCategoryDefault open={bulkSingleUpdateOpen} setOpen={setBulkSingleUpdateOpen} selectedCategories={selectedCategories} />

      <div className="mt-4 space-y-4 overflow-auto">
        <div className="flex gap-x-4">
          <ButtonInput label="Bulk Update eBay Defaults" isSubmit={false} disabled={table.getSelectedRowModel().rows.length === 0} onClick={() => handleBulkUpdate()} classes={undefined} />
          <ButtonInput label="Bulk Update Single eBay Default" isSubmit={false} disabled={table.getSelectedRowModel().rows.length === 0} onClick={() => handleBulkSingleUpdate()} classes={undefined} />
        </div>
        <table className="min-w-full bg-white overflow-scroll divide-y border divide-gray-300">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="py-3.5 px-3 text-sm font-semibold lg:table-cell text-left"
                  >
                    {header.isPlaceholder
                      ? null
                      : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: ' ▲',
                            desc: ' ▼',
                          }[header.column.getIsSorted() as string] ?? null}
                        </div>

                      )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, i) => {
              let rowCells = row.getVisibleCells();
              return (
                <Fragment key={i}>
                  <tr
                    key={row.id}
                    onClick={() => openInNewTab(`/category-editor/${row.original.id}`)}
                    // onClick={() => setCategory(row.original)}
                    className={"hover:bg-gray-100 hover:cursor-pointer"}
                  >
                    {rowCells.map((cell) => (
                      <td key={cell.id} className="px-3 py-3 border text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <></>
                    // <tr>
                    //   {/* 2nd row is a custom 1 cell row */}
                    //   <td colSpan={row.getVisibleCells().length}>
                    //     <LeadsTableSubComponent lead={row.original} />
                    //   </td>
                    // </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
